/**
 * @file: DocViewPage.js
 */
import React from 'react'
import { Helmet } from 'react-helmet-async'
import { FormattedMessage, injectIntl } from 'react-intl'
import PropTypes from 'prop-types'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import NavigationBar from '../partials/NavigationBar'
import Footer from '../partials/Footer'
import withAppStore from '../../util/withAppStore'

import AppStore from '../../stores/AppStore'
import AppDispatcher from '../../dispatchers/AppDispatcher'

import styles from '../../../css/devicePage.module.css'
import docStyles from '../../../css/doc.module.css'

function DocViewPage (props) {
  const navigate = useNavigate()
  const { docID } = useParams()
  const location = useLocation()
  const { hash } = location

  const [visited, setVisited] = React.useState({})

  const getDoc = React.useCallback(() => {
    AppDispatcher.dispatch({
      action: 'get-doc',
      docID,
      params: { sections: true },
    })
  }, [docID])

  const go = React.useCallback((hash) => {
    const path = `${location.pathname}/#${hash}`.replace('//', '/')
    navigate(path)
  }, [location.pathname, navigate])

  const goBack = React.useCallback(() => {
    const {
      data: {
        doc: {
          category,
        } = {},
      },
    } = props
    if (hash > '' && docID) {
      navigate(`/docs/${docID}`)
      return
    }
    navigate(`/docs?category=${category}`)
  }, [hash, docID, navigate, props.data?.doc?.category])

  const goBackOne = React.useCallback(() => {
    navigate(-1)
  }, [navigate])

  React.useEffect(() => {
    getDoc()
  }, [getDoc])

  React.useEffect(() => {
    setTimeout(() => window.scrollTo(0, 0), 10)
  })

  React.useEffect(() => {
    if (hash) {
      setVisited(prevVisited => ({ ...prevVisited, [hash]: true }))
    }
  }, [hash])

  const {
    auth: { logout: logoutFn },
    intl: { formatMessage },
    route: { path: page },
  } = props

  const {
    data: {
      doc,
    },
  } = AppStore

  const TOP_SECTION_HTML_ID = 'top'

  // select section that will be displayed via the page URL's hash (#)
  const sectionHtmlId = hash.substring(1) || TOP_SECTION_HTML_ID

  const logout = () => logoutFn(navigate)

  let pageContent
  const buttonStyle = {
    boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.5)',
    color: 'white',
    backgroundColor: '#284047',
    border: 'none',
    borderRadius: '9px',
    font: 'bold 22px Source Sans Pro, sans-serif',
    height: '90px',
    flexGrow: 1,
  }

  /**
   * Component to display the Next BUTTON.
   * main purpose of this component is to display
   * the bug icon so that invalid links are easily identified
   * @param {Object} param0
   */
  const NextButton = ({ nextHtmlId }) => {
    const nextSection = nextHtmlId &&
      doc.doc_sections.find((section) => section.html_id === nextHtmlId)

    const buttonText = (!!nextSection || !nextHtmlId)
      ? formatMessage({ id: 'general.next', defaultMessage: 'Next' })
      : <i className='fa fa-bug fa-4' style={{ color: 'red' }} />
    return (
      <button
        name='next'
        disabled={!nextHtmlId}
        onClick={() => go(nextHtmlId)}
        style={{ ...buttonStyle, marginLeft: '16px' }}
      >
        {buttonText}
      </button>
    )
  }

  let title

  if (doc) {
    const section = doc.doc_sections && doc.doc_sections.find((_section) => _section.html_id === sectionHtmlId)

    if (section) {
      title = section.title

      const visitedStyle = '"color: white; background: #284047"'

      let { source } = section

      for (const id of Object.keys(visited)) {
        const visitedLink = `<a href="${id}"`
        if (source.includes(visitedLink)) {
          const newLink = `<a style=${visitedStyle} href="${id}"`
          source = source.replace(visitedLink, newLink)
        }
      }

      pageContent = (
        <div className={docStyles.doc}>
          <div
            style={{ padding: '0 18px', width: 'inherit' }}
            title={title}
          >
            <div id={section.html_id} dangerouslySetInnerHTML={{ __html: source }} />
          </div>
          {section && section.html_id !== TOP_SECTION_HTML_ID && (
            <div style={{
              paddingTop: '12px',
              width: 'inherit',
              display: 'flex',
              paddingLeft: '18px',
              paddingRight: '18px',
            }}
            >
              <button name='prev' onClick={goBackOne} style={buttonStyle}>
                <FormattedMessage id='general.prev' defaultMessage='Prev' />
              </button>
              <NextButton nextHtmlId={section.next_html_id} />
            </div>
          )}
        </div>
      )
    } else {
      pageContent = <div style={{ marginLeft: 'auto', marginRight: 'auto', marginTop: '20vh', color: 'red' }}><i className='fa fa-bug fa-5x' /></div>
    }
  }

  if (!title) {
    title = doc && `${doc.title} (${doc.model_numbers})`
  }

  window.scrollTo(0, 0) // start at top

  return (
    <div>
      <Helmet><title>{title || props.pages.DocViewPage}</title></Helmet>
      <NavigationBar
        backFunc={goBack}
        name={title}
        page={page}
        logOut={logout}
      />
      <div className={styles.pageWidth} style={{ display: 'flex' }}>
        {pageContent}
      </div>
      <Footer />
    </div>
  )
}

DocViewPage.propTypes = {
  pages: PropTypes.object,
  data: PropTypes.object,
  auth: PropTypes.object,
  intl: PropTypes.object,
  route: PropTypes.object,
}

export default withAppStore(injectIntl(DocViewPage))
